import ProductVariationsEditor from './ProductVariationsEditor';
import { validateVariants } from '../../utils/productVariants';
import { wholesaleOptionsError } from "../../utils/wholesaleOptions";
// instructor/src/components/add-cource/AddCourseProductModal.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import {
  FiArrowLeft,
  FiBookOpen,
  FiBox,
  FiCheckCircle,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";
import { db, storage } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  writeBatch,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  readAffiliateTotalCommission,
  splitAffiliateCommission,
} from "../../utils/affiliateCommission";

import { DEFAULT_PRODUCT_FIELDS, resolveProductFields, buildAttributes } from "../../utils/productFields";

import StepDetails from "./StepDetails";
import StepUploadContent from "./StepUploadContent";
import StepPricingAccess from "./StepPricingAccess";
import StepReviewPreview from "./StepReviewPreview";

const isBlobUrl = (url = "") => String(url || "").startsWith("blob:");

function sanitizeFileName(name = "image") {
  return String(name || "image")
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();
}

const TYPE_META = {
  course: {
    label: "Course",
    helper: "University-style learning storefront",
    icon: FiBookOpen,
    tint: "from-amber-100 via-orange-50 to-white",
    border: "border-amber-200",
  },
  product: {
    label: "Product",
    helper: "Shopping-style product storefront",
    icon: FiBox,
    tint: "from-sky-100 via-cyan-50 to-white",
    border: "border-sky-200",
  },
  service: {
    label: "Service",
    helper: "Service package and booking storefront",
    icon: FiTrendingUp,
    tint: "from-emerald-100 via-green-50 to-white",
    border: "border-emerald-200",
  },
};

/* ========================= COMPARABLES ========================= */
function normalizeTags(tags) {
  return typeof tags === "string"
    ? tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
    : Array.isArray(tags)
      ? tags
      : [];
}

function pickCourseComparable(src = {}) {
  const affiliateCommissionAmount = readAffiliateTotalCommission(src);
  return {
    title: src.title || "",
    language: src.language || "",
    type: (src.type || "").toLowerCase(),
    category: src.category || "",
    subCategory: src.subcategory || src.subCategory || "",
    itemType: src.itemType || "",
    tags: normalizeTags(src.tags),
    price: Number(src.price || 0) || 0,
    thumbnailUrl: src.thumbnailUrl || src.thumbnail || "",
    description: src.description || "",
    youtubeLinks: Array.isArray(src.youtubeLinks) ? src.youtubeLinks : [],
    pdfLinks: Array.isArray(src.pdfLinks) ? src.pdfLinks : [],
    courseImages: Array.isArray(src.courseImages)
      ? src.courseImages
          .map((i) => (typeof i === "string" ? i : i?.url))
          .filter(Boolean)
      : [],
    downloadable: !!src.downloadable,
    pdfPreviewable: !!src.pdfPreviewable,
    affiliateEnabled: !!src.affiliateEnabled,
    affiliateCommissionAmount,
  };
}

function pickProductComparable(src = {}) {
  const affiliateCommissionAmount = readAffiliateTotalCommission(src);
  const productImages = Array.isArray(src.productImages)
    ? src.productImages
    : [];
  const images = productImages
    .map((i) => (typeof i === "string" ? i : i?.url))
    .filter(Boolean);
  const mainImg =
    productImages.find((i) => i && typeof i === "object" && i.isMain)?.url ||
    images[0] ||
    src.thumbnailUrl ||
    "";

  return {
    title: src.title || "",
    language: src.language || "",
    type: (src.type || "").toLowerCase(),
    category: src.category || "",
    subCategory: src.subcategory || src.subCategory || "",
    itemType: src.itemType || "",
    tags: normalizeTags(src.tags),
    price: Number(src.price || 0) || 0,
    thumbnailUrl: mainImg,
    description: src.productMainDesc || src.description || "",
    highlights: Array.isArray(src.productSubDescs)
      ? src.productSubDescs.filter(Boolean)
      : [],
    youtubeLinks: Array.isArray(src.productYoutubeLinks)
      ? src.productYoutubeLinks
      : [],
    images,
    variants: src.variants || [],
    detailImageIndexes: src.detailImageIndexes || [],
    sku: src.sku || "",
    stock:
      src.stock != null
        ? Number(src.stock)
        : src.inventory != null
          ? Number(src.inventory)
          : null,
    isActive: src.isActive !== false,
    isPhysical: !!src.isPhysical,
    wholesaleEnabled: !!src.wholesaleEnabled,
    wholesaleMinQty:
      src.wholesaleMinQty != null && src.wholesaleMinQty !== ""
        ? Number(src.wholesaleMinQty)
        : "",
    wholesalePrice:
      src.wholesalePrice != null && src.wholesalePrice !== ""
        ? Number(src.wholesalePrice)
        : "",
    wholesalePhone: src.wholesalePhone || "",
    wholesaleOptions: src.wholesaleOptions || [],
    affiliateEnabled: !!src.affiliateEnabled,
    affiliateCommissionAmount,
  };
}

function pickServiceComparable(src = {}) {
  const affiliateCommissionAmount = readAffiliateTotalCommission(src);
  const serviceImages = Array.isArray(src.serviceImages)
    ? src.serviceImages
    : [];
  const images = serviceImages
    .map((i) => (typeof i === "string" ? i : i?.url))
    .filter(Boolean);
  const mainImg = src.thumbnailUrl || images[0] || "";

  return {
    title: src.title || "",
    type: (src.type || "").toLowerCase(),
    category: src.category || "",
    subCategory: src.subcategory || src.subCategory || "",
    itemType: src.itemType || "",
    tags: normalizeTags(src.tags),
    price: Number(src.price || 0) || 0,
    thumbnailUrl: mainImg,
    description: src.serviceDesc || src.description || "",
    deliverables: Array.isArray(src.serviceDeliverables)
      ? src.serviceDeliverables.filter(Boolean)
      : [],
    faqs: Array.isArray(src.serviceFaqs) ? src.serviceFaqs.filter(Boolean) : [],
    youtubeLinks: Array.isArray(src.serviceYoutubeLinks)
      ? src.serviceYoutubeLinks
      : [],
    images,
    contact: Array.isArray(src.contactMethods) ? src.contactMethods : [],
    affiliateEnabled: !!src.affiliateEnabled,
    affiliateCommissionAmount,
  };
}

/* ========================= NORMALIZERS ========================= */
function normalizeCourseImages(arr) {
  return Array.isArray(arr)
    ? arr.map((i) =>
        typeof i === "string" ? { url: i } : { url: i?.url ?? "" },
      )
    : [];
}
function normalizeProductImages(arr) {
  return Array.isArray(arr)
    ? arr.map((i) =>
        typeof i === "string"
          ? { url: i, isMain: false }
          : { url: i?.url ?? "", isMain: !!i?.isMain },
      )
    : [];
}
function normalizeServiceImages(arr) {
  return Array.isArray(arr)
    ? arr.map((i) =>
        typeof i === "string" ? { url: i } : { url: i?.url ?? "" },
      )
    : [];
}

function normalizeListingDetails(src = {}) {
  return {
    ...src,
    subcategory: src.subcategory || src.subCategory || "",
    subCategory: src.subCategory || src.subcategory || "",
    itemType: src.itemType || "",
    productYoutubeLinks: (Array.isArray(src.productYoutubeLinks) ? src.productYoutubeLinks : Array.isArray(src.youtubeLinks) ? src.youtubeLinks : []).map(link => typeof link === "string" ? { url: link, notes: "" } : link),
    productMainDesc: src.productMainDesc || src.description || "",
    serviceDesc: src.serviceDesc || src.description || "",
    stock:
      src.stock != null && src.stock !== ""
        ? Number(src.stock)
        : src.inventory != null && src.inventory !== ""
          ? Number(src.inventory)
          : src.stock ?? src.inventory ?? null,
  };
}

/* ========================= SAFE MERGE ========================= */
function safeMerge(prev, partial) {
  const next = { ...prev, ...partial };
  const protect = (k) => {
    if (
      Array.isArray(prev[k]) &&
      prev[k].length > 0 &&
      Array.isArray(partial[k]) &&
      partial[k].length === 0
    ) {
      next[k] = prev[k];
    }
  };
  [
    // course
    "courseImages",
    "pdfLinks",
    "youtubeLinks",
    // product
    "productImages",
    "productSubDescs",
    // service
    "serviceImages",
    "serviceDeliverables",
    "serviceFaqs",
    "serviceYoutubeLinks",
    "contactMethods",
  ].forEach(protect);
  return next;
}

/* ========================= MAIN ========================= */
/**
 * NEW PROPS:
 * - storeLimits: { hasActive, courseLimit, productLimit, serviceLimit }
 * - storeCounts: { course, product, service }
 */
export default function AddCourseProductModal({
  onClose,
  editData,
  initialType = "",
  storeLimits,
  storeCounts,
  storeBusinessType = "",
  productBankReady = true,
}) {
  const [details, setDetails] = useState(() =>
    editData
      ? {
          ...normalizeListingDetails(editData),
          __docId: editData.id,
          courseImages: normalizeCourseImages(editData.courseImages),
          productImages: normalizeProductImages(
            editData.productImages ?? editData.images,
          ),
          serviceImages: normalizeServiceImages(editData.serviceImages),
          affiliateCommissionAmount: readAffiliateTotalCommission(editData),
        }
      : {
          // common
          title: "",
          language: "",
          type: "",
          category: "",
          subcategory: "",
          itemType: "",
          description: "",
          tags: "",
          price: "",
          thumbnail: "",
          affiliateEnabled: false,
          affiliateCommissionAmount: 0,
          affiliateLevel1Amount: 0,
          affiliateLevel2Amount: 0,
          affiliateLevel3Amount: 0,

          // course
          pdfLinks: [],
          youtubeLinks: [],
          courseImages: [],
          downloadable: false,
          pdfPreviewable: false,

          // product
          productImages: [],
          productMainDesc: "",
          productSubDescs: [],
          productYoutubeLinks: [],
          sku: "",
          stock: null,
          isActive: true,
          isPhysical: false,
          wholesaleEnabled: false,
          wholesaleMinQty: "",
          wholesalePrice: "",
          wholesalePhone: "",

          // service
          serviceDesc: "",
          serviceImages: [],
          serviceDeliverables: [],
          serviceFaqs: [],
          serviceYoutubeLinks: [],
          contactMethods: [],
        },
  );

  const [type, setType] = useState(editData?.type || initialType || "");
  const [step, setStep] = useState(editData || initialType ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [productFields, setProductFields] = useState(DEFAULT_PRODUCT_FIELDS);
  const [settingsError, setSettingsError] = useState("");
  useEffect(() => onSnapshot(doc(db, "listing_settings", "defaults"), (snapshot) => {
    setProductFields(resolveProductFields(snapshot.data() || {}));
    setSettingsError("");
  }, (error) => setSettingsError(error.message)), []);
  const submittingRef = useRef(false);
  const draftIdRef = useRef(editData?.id || null);
  const initialComparableRef = useRef(null);
  const initialStatusRef = useRef(editData?.status ?? null);

  const mergeDetails = (partial) =>
    setDetails((prev) => safeMerge(prev, partial));

  const currentType = useMemo(
    () => String(details.type || type || "").toLowerCase(),
    [details.type, type],
  );

  /* -------------------- LIMIT GATE (NEW) -------------------- */
  function gateForType(t) {
    // Allow editing always (only block NEW)
    if (editData) return { ok: true };

    if (!storeLimits?.hasActive) {
      return { ok: false, msg: "You need an active store package to post." };
    }

    const counts = storeCounts || { course: 0, product: 0, service: 0 };
    const limCourse = Number(storeLimits?.courseLimit || 0);
    const limProduct = Number(storeLimits?.productLimit || 0);
    const limService = Number(storeLimits?.serviceLimit || 0);
    const lockedType = String(storeBusinessType || "").toLowerCase();

    if (lockedType && lockedType !== t) {
      return {
        ok: false,
        msg: `This store is locked to ${lockedType}s. Buy more packages to grow that same store type.`,
      };
    }

    if (t === "course" && limCourse && counts.course >= limCourse) {
      return {
        ok: false,
        msg: `Course limit reached (${limCourse}). Please upgrade your package.`,
      };
    }
    if (t === "product" && limProduct && counts.product >= limProduct) {
      return {
        ok: false,
        msg: `Product limit reached (${limProduct}). Please upgrade your package.`,
      };
    }
    if (t === "service" && limService && counts.service >= limService) {
      return {
        ok: false,
        msg: `Service limit reached (${limService}). Please upgrade your package.`,
      };
    }
    return { ok: true };
  }

  function startType(t) {
    if (t === "product" && !productBankReady) {
      alert("Please add your bank details and try again.");
      return;
    }
    const gate = gateForType(t);
    if (!gate.ok) {
      alert(gate.msg);
      return;
    }
    setType(t);
    mergeDetails({ type: t });
    setStep(2);
  }

  /* -------- hydrate full doc when editing -------- */
  useEffect(() => {
    if (!editData?.id) return;

    setType(editData.type);
    draftIdRef.current = editData.id;
    setStep(2);

    const needsHydrate =
      !Array.isArray(editData.courseImages) ||
      !Array.isArray(editData.productImages) ||
      !Array.isArray(editData.serviceImages) ||
      !Array.isArray(editData.youtubeLinks) ||
      !Array.isArray(editData.pdfLinks);

    (async () => {
      try {
        if (!needsHydrate) {
          setDetails((s) => ({
            ...s,
            ...normalizeListingDetails(editData),
            __docId: editData.id,
            courseImages: normalizeCourseImages(editData.courseImages),
            productImages: normalizeProductImages(
              editData.productImages ?? editData.images,
            ),
            serviceImages: normalizeServiceImages(editData.serviceImages),
          }));

          const kind = (editData.type || type || "").toLowerCase();
          initialComparableRef.current =
            kind === "course"
              ? pickCourseComparable(editData)
              : kind === "product"
                ? pickProductComparable({
                    ...editData,
                    productImages:
                      editData.productImages ??
                      (Array.isArray(editData.images)
                        ? editData.images.map((u) => ({ url: u }))
                        : []),
                  })
                : pickServiceComparable({ ...editData });
          initialStatusRef.current =
            editData.status ?? initialStatusRef.current;
          return;
        }

        const snap = await getDoc(doc(db, "courses", editData.id));
        if (snap.exists()) {
          const full = { id: snap.id, ...snap.data() };
          setDetails((s) =>
            safeMerge(s, {
              ...normalizeListingDetails(full),
              __docId: full.id,
              courseImages: normalizeCourseImages(full.courseImages),
              productImages: normalizeProductImages(
                full.productImages ?? full.images,
              ),
              serviceImages: normalizeServiceImages(full.serviceImages),
            }),
          );

          const kind = (full.type || type || "").toLowerCase();
          initialComparableRef.current =
            kind === "course"
              ? pickCourseComparable(full)
              : kind === "product"
                ? pickProductComparable({
                    ...full,
                    productImages:
                      full.productImages ??
                      (Array.isArray(full.images)
                        ? full.images.map((u) => ({ url: u }))
                        : []),
                  })
                : pickServiceComparable(full);
          initialStatusRef.current = full.status ?? initialStatusRef.current;
        }
      } catch (e) {
        console.warn("Hydrate edit doc failed:", e);
        // Fallback
        setDetails((s) => ({
          ...s,
          ...normalizeListingDetails(editData),
          __docId: editData.id,
          courseImages: normalizeCourseImages(editData.courseImages),
          productImages: normalizeProductImages(
            editData.productImages ?? editData.images,
          ),
          serviceImages: normalizeServiceImages(editData.serviceImages),
        }));
        const kind = (editData.type || type || "").toLowerCase();
        initialComparableRef.current =
          kind === "course"
            ? pickCourseComparable(editData)
            : kind === "product"
              ? pickProductComparable({
                  ...editData,
                  productImages:
                    editData.productImages ??
                    (Array.isArray(editData.images)
                      ? editData.images.map((u) => ({ url: u }))
                      : []),
                })
              : pickServiceComparable(editData);
        initialStatusRef.current = editData.status ?? initialStatusRef.current;
      }
    })();
  }, [editData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editData || !initialType) return;
    setType(initialType);
    setDetails((prev) => ({ ...prev, type: initialType }));
    setStep(2);
  }, [editData, initialType]);

  function isValidForSubmit() {
    return (details.type || type) && details.title;
  }

  /* ------------------------ SUBMIT ------------------------ */
  const handleSubmit = async () => {
    if (!isValidForSubmit()) {
      alert("Title and type are required!");
      return;
    }
    const user = getAuth().currentUser;
    if (!user) {
      alert("You must be logged in.");
      return;
    }

    // NEW: limit gate again (for safety)
    if (!editData) {
      if (currentType === "product" && !productBankReady) {
        alert("Please add your bank details and try again.");
        return;
      }
      const g = gateForType(currentType);
      if (!g.ok) {
        alert(g.msg);
        return;
      }
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    try {
      if (!draftIdRef.current) {
        draftIdRef.current = `${user.uid}_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`;
      }

      const kind = currentType;
      const isProduct = kind === "product";
      const isCourse = kind === "course";
      const isService = kind === "service";
      let attributes = [];
      if (isProduct) {
        const settings = await getDoc(doc(db, "listing_settings", "defaults"));
        attributes = buildAttributes(resolveProductFields(settings.data() || {}),
          details.category, details.productFieldValues, details.attributes || []);
      }

      const wholesaleOptions = [...new Set((Array.isArray(details.wholesaleOptions) ? details.wholesaleOptions : String(details.wholesaleOptions || "").split("\n")).map((v) => String(v).trim()).filter(Boolean))];
      if (isProduct && details.wholesaleEnabled && !(details.variants || []).length) {
        const optionError = wholesaleOptionsError({ wholesaleOptions, attributes });
        if (optionError) throw new Error(optionError);
      }
      const id = draftIdRef.current;
      const refDoc = doc(collection(db, "courses"), id);

      const variationError = isProduct ? validateVariants(details.variants || []) : '';
      if (variationError) throw new Error(variationError);
      const variants = isProduct ? await Promise.all((details.variants || []).map(async (variant) => {
        const images = await Promise.all((variant.images || []).map(async (image,index) => {
          if (image?.file instanceof File) {
            if (!['image/jpeg','image/png','image/webp'].includes(image.file.type) || image.file.size >= 10*1024*1024) throw new Error('Variation images must be JPG, PNG or WebP under 10 MB.');
            const imageRef=ref(storage,`products/${user.uid}/${id}/variants/${variant.id}/${Date.now()}-${index}-${sanitizeFileName(image.file.name)}`);
            await uploadBytes(imageRef,image.file);return getDownloadURL(imageRef);
          }
          return typeof image === 'string' ? image : image?.url || '';
        }));
        return {id:variant.id,options:Object.fromEntries(Object.entries(variant.options).map(([key,value])=>[key.trim(),String(value).trim()])),price:Number(variant.price),originalPrice:variant.originalPrice===''||variant.originalPrice==null?null:Number(variant.originalPrice),stock:Number(variant.stock),sku:String(variant.sku||'').trim(),enabled:variant.enabled!==false,detailImageIndexes:images.map((url,index)=>({url,index})).filter(entry=>entry.url&&!isBlobUrl(entry.url)).flatMap((entry,index)=>(variant.detailImageIndexes||[]).includes(entry.index)?[index]:[]),images:images.filter(url=>url&&!isBlobUrl(url))};
      })) : [];
      const enabledVariants=variants.filter(v=>v.enabled);
      /* ---- images ---- */
      const courseImagesRaw = Array.isArray(details.courseImages)
        ? details.courseImages
        : [];
      const courseImages = courseImagesRaw
        .map((i) => (typeof i === "string" ? i : i?.url))
        .filter(Boolean);

      const productImagesRaw = Array.isArray(details.productImages)
        ? details.productImages
        : [];
      const productImagesPrepared = await Promise.all(
        productImagesRaw.map(async (item, index) => {
          const img =
            typeof item === "string" ? { url: item, isMain: false } : item || {};
          let finalUrl = img.url || "";

          if (img.file instanceof File) {
            if (!img.file.type.startsWith("image/") || img.file.size >= 10 * 1024 * 1024) {
              throw new Error(`Image "${img.file.name}" must be an image smaller than 10 MB.`);
            }
            const ext =
              sanitizeFileName(img.file.name || "").split(".").pop() || "jpg";
            const safeBase = sanitizeFileName(
              img.file.name?.replace(/\.[^.]+$/, "") || `product-image-${index + 1}`,
            );
            const imageRef = ref(
              storage,
              `products/${user.uid}/${id}/${Date.now()}_${index}_${safeBase}.${ext}`,
            );
            await uploadBytes(imageRef, img.file);
            finalUrl = await getDownloadURL(imageRef);
          }

          if (!finalUrl || isBlobUrl(finalUrl)) return null;

          return {
            url: finalUrl,
            isMain: !!img.isMain,
          };
        }),
      );
      const productImages = productImagesPrepared.filter(Boolean);
      if (!productImages.length && variants.some(v=>v.images.length)) productImages.push({url:variants.find(v=>v.images.length).images[0],isMain:true});
      if (productImages.length > 0 && !productImages.some((i) => i.isMain)) {
        productImages[0] = { ...productImages[0], isMain: true };
      }
      const productMainImg =
        productImages.find((i) => i.isMain)?.url || productImages[0]?.url || "";

      const serviceImagesRaw = Array.isArray(details.serviceImages)
        ? details.serviceImages
        : [];
      const serviceImages = serviceImagesRaw
        .map((i) => (typeof i === "string" ? i : i?.url))
        .filter(Boolean);
      const serviceMainImg =
        details.thumbnailUrl || details.thumbnail || serviceImages[0] || "";

      if (isProduct && !productMainImg) {
        alert("Please add/select at least one product image.");
        setLoading(false);
        submittingRef.current = false;
        return;
      }

      /* ---- price ---- */
      const basePrice = enabledVariants.length ? Math.min(...enabledVariants.map(v=>v.price)) : Number(details.price || 0) || 0;
      const affiliateEnabled = !!details.affiliateEnabled;
      const affiliateCommissionAmount = affiliateEnabled
        ? Number(details.affiliateCommissionAmount || 0) || 0
        : 0;
      const affiliateSplit = splitAffiliateCommission(affiliateCommissionAmount);
      const affiliateLevel1Amount = affiliateEnabled ? affiliateSplit.byLevel[1] : 0;
      const affiliateLevel2Amount = affiliateEnabled ? affiliateSplit.byLevel[2] : 0;
      const affiliateLevel3Amount = affiliateEnabled ? affiliateSplit.byLevel[3] : 0;

      // Listings now publish immediately. We keep the saved status consistent
      // across new creates and edits so shop owners never need admin approval
      // before the item appears on their live storefront.
      const finalStatus = "approved";

      const payloadBase = {
        title: details.title,
        language: details.language || "",
        type: kind,
        category: details.category || "",
        subCategory: details.subcategory || details.subCategory || "",
        itemType: String(details.itemType || "").trim(),
        tags: normalizeTags(details.tags),
        price: basePrice,
        finalPrice: basePrice,
        basePrice,
        originalPrice: basePrice,
        createdBy: details.createdBy || user.uid,
        createdAt: details.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: finalStatus,
        approvedAt: serverTimestamp(),
        isProduct,
        isCourse,
        isService,
        affiliateEnabled,
        affiliateCommissionAmount,
        affiliateLevel1Amount,
        affiliateLevel2Amount,
        affiliateLevel3Amount,
        affiliateTotalCommissionAmount: affiliateCommissionAmount,
        affiliateSplitModel: affiliateEnabled ? "fixed_50_30_20" : null,
        affiliateCommissionByLevel: affiliateEnabled
          ? affiliateSplit.byLevel
          : { 1: 0, 2: 0, 3: 0 },
      };

      const payloadCourse = isCourse
        ? {
            ...payloadBase,
            thumbnailUrl:
              details.thumbnailUrl ||
              details.thumbnail ||
              courseImages[0] ||
              "",
            description: details.description || "",
            youtubeLinks: Array.isArray(details.youtubeLinks)
              ? details.youtubeLinks
              : [],
            pdfLinks: Array.isArray(details.pdfLinks) ? details.pdfLinks : [],
            courseImages,
            downloadable: !!details.downloadable,
            pdfPreviewable: !!details.pdfPreviewable,
          }
        : {};

      const productImageUrls = productImages.map((i) => i.url);
      const productStock = variants.length ? enabledVariants.reduce((sum,v)=>sum+v.stock,0) :
        details.stock != null && details.stock !== ""
          ? Number(details.stock)
          : details.inventory != null && details.inventory !== ""
            ? Number(details.inventory)
            : null;
      const payloadProduct = isProduct
        ? {
            ...payloadBase,
            thumbnailUrl: productMainImg,
            images: productImageUrls,
            gallery: productImageUrls,
            description: details.productMainDesc || details.description || "",
            highlights: Array.isArray(details.productSubDescs)
              ? details.productSubDescs.filter(Boolean)
              : [],
            youtubeLinks: Array.isArray(details.productYoutubeLinks)
              ? details.productYoutubeLinks
              : [],
            sku: details.sku || "",
            variants,
            stock: productStock,
            inventory: productStock,
            isActive: details.isActive !== false,
            isPhysical: !!details.isPhysical,
            wholesaleEnabled: !!details.wholesaleEnabled,
            wholesaleMinQty: details.wholesaleEnabled
              ? Number(details.wholesaleMinQty || 0) || 0
              : null,
            wholesalePrice: details.wholesaleEnabled
              ? Number(details.wholesalePrice || 0) || 0
              : null,
            wholesaleOptions: details.wholesaleEnabled ? wholesaleOptions : [],
            wholesalePhone: details.wholesaleEnabled
              ? String(details.wholesalePhone || "").trim()
              : "",
            productImages,
            detailImageIndexes: productImagesPrepared.map((image,index)=>({image,index})).filter(entry=>entry.image).flatMap((entry,index)=>(details.detailImageIndexes||[]).includes(entry.index)?[index]:[]),
            attributes,
          }
        : {};

      const payloadService = isService
        ? {
            ...payloadBase,
            thumbnailUrl: serviceMainImg,
            description: details.serviceDesc || details.description || "",
            deliverables: Array.isArray(details.serviceDeliverables)
              ? details.serviceDeliverables.filter(Boolean)
              : [],
            faqs: Array.isArray(details.serviceFaqs)
              ? details.serviceFaqs.filter(Boolean)
              : [],
            youtubeLinks: Array.isArray(details.serviceYoutubeLinks)
              ? details.serviceYoutubeLinks
              : [],
            images: serviceImages,
            serviceImages: normalizeServiceImages(details.serviceImages),
            contactMethods: Array.isArray(details.contactMethods)
              ? details.contactMethods
              : [],
          }
        : {};

      // Commit the listing and first-store setup together so a failed setup
      // cannot leave a saved item behind while showing a save error.
      const batch = writeBatch(db);
      batch.set(refDoc, { ...payloadCourse, ...payloadProduct, ...payloadService }, { merge: true });
      if (!editData) {
        if (!storeBusinessType) {
          batch.set(doc(db, "users", user.uid), {
            storeBusinessType: kind,
            storeModeLockedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
        batch.set(doc(db, "stores", user.uid), {
          instructorId: user.uid,
          businessType: kind,
          storeBusinessType: kind,
          updatedAt: serverTimestamp(),
        }, { merge: true });
      }
      await batch.commit();

      alert("Your listing is now live in your shop. 🚀");
      onClose && onClose();
    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save listing: ${err.message || "Please try again."}`);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  /* ------------------------ RENDER ------------------------ */
  const gateCourse = gateForType("course");
  const gateProduct = gateForType("product");
  const gateService = gateForType("service");
  const currentMeta = TYPE_META[currentType] || null;
  const isTypePreselected = !!initialType && !editData;
  const stepTitle =
    step === 1
      ? "Choose Your Listing Type"
      : step === 2
        ? `Enter ${currentMeta?.label || "Listing"} Details`
        : step === 3
          ? `Upload ${currentMeta?.label || "Listing"} Content`
          : step === 4
            ? "Set Pricing And Affiliate Share"
            : "Review Before Submission";
  const stepSubtitle =
    step === 1
      ? "Pick the store path you want to build. Once the first listing is submitted, that path becomes locked for this store."
      : isTypePreselected
        ? `${currentMeta?.label || "This listing"} is already selected from your slot board, so Back will return you to the slot layout.`
        : "Complete each step carefully, then send the listing for review.";
  const progressItems = [
    "Choose type",
    "Details",
    "Content",
    "Pricing",
    "Review",
  ];
  const handleStepTwoBack = () => {
    if (isTypePreselected) {
      onClose?.();
      return;
    }
    setStep(1);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div className="relative flex h-[95vh] w-full max-w-[1500px] flex-col overflow-hidden rounded-[36px] border border-white/70 bg-gradient-to-br from-[#fff9ef] via-white to-[#f4ece2] shadow-[0_30px_120px_rgba(15,23,42,0.24)] transition-all">
        <button
          onClick={onClose}
          className="absolute right-6 top-6 z-20 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white/90 text-xl text-slate-500 shadow-sm transition hover:text-slate-900"
        >
          <FiX />
        </button>

        <div className="border-b border-amber-100 bg-white/70 px-6 py-6 backdrop-blur md:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex rounded-full border border-amber-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
                Listing Builder
              </div>
              <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-900">
                {stepTitle}
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-600 md:text-base">
                {stepSubtitle}
              </p>
            </div>

            <div className="rounded-[28px] border border-white/80 bg-white/90 p-4 shadow-sm lg:min-w-[320px]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                    Step Progress
                  </div>
                  <div className="mt-1 text-lg font-black text-slate-900">
                    Step {step} / 5
                  </div>
                </div>
                {currentMeta ? (
                  <div className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
                    <currentMeta.icon />
                    {currentMeta.label}
                  </div>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {progressItems.map((label, index) => {
                  const itemStep = index + 1;
                  const done = itemStep < step;
                  const active = itemStep === step;
                  return (
                    <div
                      key={label}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold ${
                        done
                          ? "bg-emerald-100 text-emerald-700"
                          : active
                            ? "bg-slate-900 text-white"
                            : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {done ? <FiCheckCircle /> : <span>{itemStep}</span>}
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {settingsError && <p role="alert" className="px-6 text-red-600">Could not load product field settings: {settingsError}</p>}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-8 md:py-8">
          {step === 1 && (
            <div className="grid gap-5 lg:grid-cols-3">
              <button
                onClick={() => startType("course")}
                disabled={!gateCourse.ok}
                className={`w-full rounded-[28px] border bg-gradient-to-br p-8 text-left transition hover:-translate-y-1 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 ${TYPE_META.course.tint} ${TYPE_META.course.border}`}
                title={!gateCourse.ok ? gateCourse.msg : ""}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white bg-white/90 text-slate-900 shadow-sm">
                  <FiBookOpen className="text-2xl" />
                </div>
                <div className="mt-8 text-3xl font-black text-slate-900">
                  Course
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Build a learning storefront with lessons, PDFs, and premium educational content.
                </div>
              </button>

              <button
                onClick={() => startType("product")}
                disabled={!gateProduct.ok}
                className={`w-full rounded-[28px] border bg-gradient-to-br p-8 text-left transition hover:-translate-y-1 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 ${TYPE_META.product.tint} ${TYPE_META.product.border}`}
                title={!gateProduct.ok ? gateProduct.msg : ""}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white bg-white/90 text-slate-900 shadow-sm">
                  <FiBox className="text-2xl" />
                </div>
                <div className="mt-8 text-3xl font-black text-slate-900">
                  Product
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Create a shopping storefront for physical or digital products with image galleries.
                </div>
              </button>

              <button
                onClick={() => startType("service")}
                disabled={!gateService.ok}
                className={`w-full rounded-[28px] border bg-gradient-to-br p-8 text-left transition hover:-translate-y-1 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 ${TYPE_META.service.tint} ${TYPE_META.service.border}`}
                title={!gateService.ok ? gateService.msg : ""}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white bg-white/90 text-slate-900 shadow-sm">
                  <FiTrendingUp className="text-2xl" />
                </div>
                <div className="mt-8 text-3xl font-black text-slate-900">
                  Service
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-600">
                  Offer service packages, consulting, and custom client work through your store.
                </div>
              </button>

              {!editData && !storeLimits?.hasActive ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-2xl p-4 lg:col-span-3">
                  You need an active store package to post.
                </div>
              ) : null}

              {!editData && storeBusinessType ? (
                <div className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-2xl p-4 lg:col-span-3">
                  Your store is locked to <span className="font-semibold">{storeBusinessType}</span> listings because that was the first business type you chose.
                </div>
              ) : null}
            </div>
          )}

          {step === 2 && (
            <div className="rounded-[30px] border border-white/80 bg-white/90 p-5 shadow-sm md:p-6">
              <StepDetails
                type={currentType}
                values={details}
                productFields={productFields}
                onChange={mergeDetails}
                onNext={() => setStep(3)}
                onBack={handleStepTwoBack}
              />
            </div>
          )}

          {step === 3 && (
            <div className="rounded-[30px] border border-white/80 bg-white/90 p-5 shadow-sm md:p-6">
              {currentType === 'product' && <ProductVariationsEditor values={details} onChange={patch=>{
                const enabled=(patch.variants||[]).filter(v=>v.enabled!==false);
                const prices=enabled.filter(v=>v.price!==''&&Number.isFinite(Number(v.price))).map(v=>Number(v.price));
                mergeDetails({...patch,...(prices.length?{price:Math.min(...prices)}:{}),...(patch.variants?.length?{stock:enabled.reduce((sum,v)=>sum+(Number(v.stock)||0),0)}:{})});
              }} />}
              <StepUploadContent
                type={currentType}
                values={details}
                onChange={mergeDetails}
                onNext={() => setStep(4)}
                onBack={() => setStep(2)}
              />
            </div>
          )}

          {step === 4 && (
            <div className="rounded-[30px] border border-white/80 bg-white/90 p-5 shadow-sm md:p-6">
              <StepPricingAccess
                type={currentType}
                values={details}
                onChange={mergeDetails}
                onNext={() => setStep(5)}
                onBack={() => setStep(3)}
              />
            </div>
          )}

          {step === 5 && (
            <div className="rounded-[30px] border border-white/80 bg-white/90 p-5 shadow-sm md:p-6">
              <StepReviewPreview
                type={currentType}
                values={details}
                onBack={() => setStep(4)}
                onSubmit={handleSubmit}
                loading={loading}
              />
            </div>
          )}
        </div>

        {isTypePreselected && step >= 2 ? (
          <div className="border-t border-white/70 bg-white/75 px-6 py-4 backdrop-blur md:px-8">
            <button
              type="button"
              onClick={handleStepTwoBack}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm"
            >
              <FiArrowLeft />
              Back To Slots
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
